import { Component, DestroyRef, OnInit, inject, signal, computed } from '@angular/core';

import { ReservationService } from './reservation.service';
import { DateService } from '@/common/date.service';
import { LoaderService } from '@/common/loader/loader.service';

import { Channel, Offer, Reservation, SeatNumberPayload, Slot } from './reservation.model';

import { ActivatedRoute } from '@angular/router';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { ReservationSummaryApiService } from './reservation-summary/services/reservation-summary-api.service';
import { OskarOfferUrlBuilder } from '@/common/oskar-offer-url-builder';
import { CancellationStatuses, Status } from '@/common/enums/Status';
import { ReservationSummaryStoreService } from './reservation-summary/services/reservation-summary-store.service';
import { BehaviorSubject, filter, switchMap, take } from 'rxjs';
import { TransportKind } from '@/common/enums/TransportKind';
import { AuthService } from '@/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { ReservationSummaryCommissionEditDialogComponent } from '@/reservation/reservation-summary/reservation-summary-commission-edit-dialog/reservation-summary-commission-edit-dialog.component';
import { COMFORT_PACKAGE_SERVICE_ID, SelectedSeatsOutput } from '@oskar-frontend/comfort-package';
import { deepCompare } from '@/_utils/deep-compare';
import { ProductKind } from '@/common/product-kind.enum';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { QuestionService } from '@/common/question-dialog/question-dialog.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Permissions } from '@/common/enums/Permissions';
import { VouchersApiService } from '@/vouchers/_api/vouchers.api.service';
import { ReservationApiService } from './reservation.api.service';
import { Voucher } from '@/vouchers/_model/voucher.model';
import { Insurer } from '@/codebook/insurer/_types/insurer.type';

@Component({
	selector: 'app-reservation',
	templateUrl: './reservation.component.html',
	styleUrls: ['./reservation.component.scss'],
	providers: [ReservationApiService],
})
export class ReservationComponent implements OnInit {
	constructor(
		protected _service: ReservationService,
		protected dateService: DateService,
		private readonly loaderService: LoaderService,
		private readonly route: ActivatedRoute,
		private readonly infobox: InfoboxService,
		private readonly apiService: ReservationSummaryApiService,
		private readonly store: ReservationSummaryStoreService,
		private readonly authService: AuthService,
		private readonly dialog: MatDialog,
		private readonly questionService: QuestionService,
	) {}

	reservation: Reservation = null;

	comfortPackageDisabled = true;

	saveSeatsBtnDisabled$ = new BehaviorSubject<boolean>(true);

	voucherCode = '';
	voucherValue = 0;

	readonly ProductKind = ProductKind;

	readonly isAdmin = computed(() => this.authService.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM]);

	readonly COMFORT_PACKAGE_LABEL = 'Pakiet komfort';

	protected readonly parseInt = parseInt;
	readonly activeReservationInsurer = signal<Insurer>(null);

	protected reservationId: number = null;
	protected channels: Promise<Channel[]>;

	private chosenSeats: SelectedSeatsOutput;
	private activeTab: MatTabChangeEvent;
	private seatsModified = false;

	private readonly destroyRef = inject(DestroyRef);
	private readonly voucherApiService = inject(VouchersApiService);
	private readonly reservationApiService = inject(ReservationApiService);

	ngOnInit(): void {
		if (!this.route.snapshot.params.id) {
			return this.infobox.error('Nie znaleziono rezerwacji o tym identyfikatorze');
		}

		this.reservationId = this.route.snapshot.params.id;
		this.loaderService.show();
		this._service.loadCurrencies();
		this._service.loadNationalities();
		this.getReservationData();
	}

	showCommissionEditDialog(): void {
		this.dialog
			.open(ReservationSummaryCommissionEditDialogComponent, {
				data: {
					reservationId: this.reservationId,
					currentCommissionValue: this.reservation.commissionValue,
					reservationTotalPrice: this.reservation.totalPrice,
				},
				minWidth: '450px',
			})
			.afterClosed()
			.subscribe(() => {
				this.loaderService.show();
				this.getReservationData();
			});
	}

	onSelectedTabChange(event: MatTabChangeEvent): void {
		if (this.activeTab && this.activeTab.tab.textLabel === this.COMFORT_PACKAGE_LABEL && this.seatsModified) {
			this.saveSeatsBtnDisabled$
				.pipe(
					take(1),
					filter((value) => !value),
					switchMap(() =>
						this.questionService.ask('Masz niezapisane zmiany siedzeń, czy chcesz zapisać zmiany?').pipe(
							take(1),
							filter((response) => response.result),
						),
					),
				)
				.subscribe(() => {
					this.saveSeats();
				});
		}

		this.activeTab = event;
	}

	submitVoucher(): void {
		this.voucherApiService
			.isVoucherValid(this.voucherCode)
			.pipe(
				filter((voucher) => {
					if (!voucher.isValid) {
						this.infobox.error('Voucher nie jest ważny');
					}
					return voucher.isValid;
				}),
				switchMap((voucher) => {
					this.voucherValue = voucher.methodValue;
					return this.reservationApiService.updateReservation(this.reservationId, { voucher: voucher.code });
				}),
			)
			.subscribe(() => {
				this.infobox.success('Voucher został dodany poprawnie do rezerwacji');
				this.refetchReservation();
			});
	}

	deleteVoucher(): void {
		this.reservationApiService.updateReservation(this.reservationId, { voucher: null }).subscribe(() => {
			this.voucherCode = '';
			this.voucherValue = 0;
			this.refetchReservation();
		});
	}

	daysBetween(startDate: string, endDate: string): number {
		return Math.floor(this.dateService.daysBetween(startDate, endDate));
	}

	confirmSeats({ chosenSeats, source }: { chosenSeats: SelectedSeatsOutput; source: 'click' | 'trigger' }): void {
		if (source === 'click') {
			this.seatsModified = true;
		}

		this.chosenSeats = {
			...this.chosenSeats,
			...chosenSeats,
		};

		const slots = this.activeSlotsFromOffers;

		if (
			this.chosenSeats.doubleDeckDeparture?.length === slots.length &&
			this.chosenSeats.doubleDeckReturn?.length === slots.length &&
			this.chosenSeats.singleDeckDeparture?.length === slots.length &&
			this.chosenSeats.singleDeckReturn?.length === slots.length
		) {
			this.saveSeatsBtnDisabled$.next(false);
		} else {
			this.saveSeatsBtnDisabled$.next(true);
		}
	}

	async saveSeats(): Promise<void> {
		try {
			if (this.saveSeatsBtnDisabled$.getValue()) {
				return;
			}

			const slots = this.activeSlotsFromOffers;
			const payload = slots.map((s, i) => this.mapSlot(s, i)).filter((s) => !!s);

			if (!payload.length) {
				return;
			}

			const patchPayload = payload.filter((p) => p.id);
			const postPayload = payload.filter((p) => !p.id);

			if (patchPayload.length) {
				await this.apiService.actions().patchSeatNumber(patchPayload);
			}

			if (postPayload.length) {
				await Promise.all(postPayload.map((p) => this.apiService.actions().postSeatNumber(p)));
			}

			this.infobox.success('Miejsca zostały zapisane pomyślnie');
			this.seatsModified = false;
			await this.refetchReservation();
		} catch (err: unknown) {
			this.infobox.error('Wystąpił błąd podczas zapisu miejsc');
		}
	}

	protected changeChannel(channel: number): void {
		this.apiService
			.actions()
			.patchReservation({ id: this.reservationId, channel: channel })
			.then(() => this.getReservationData());
	}

	protected countNights(lowerDate, upperDate): number | string {
		const countLowerDate: any = new Date(lowerDate);
		const countUpperDate: any = new Date(upperDate);
		countUpperDate.setDate(countUpperDate.getDate() - 1);
		if (!lowerDate && !upperDate) {
			return '';
		}
		return Math.ceil(Math.abs(countLowerDate - countUpperDate) / (1000 * 60 * 60 * 24));
	}

	protected get isTourNumber(): string {
		return this.reservation.istourNumber !== null ? `(I: ${this.reservation.istourNumber})` : '';
	}

	protected get offer(): Offer {
		const offer = this.reservation.offer.find((v) => !CancellationStatuses.includes(v?.status as Status));
		return offer ? offer : this.reservation.offer[0];
	}

	protected get product(): string {
		return this.offer?.product ? `${this.offer?.product.code} ${this.offer?.product.name}` : '';
	}

	protected get offerUrl(): string {
		const urlBuilder = new OskarOfferUrlBuilder();
		if (this.offer.term.dateRange.lower && this.offer.term.dateRange.upper) {
			urlBuilder
				.setOfferUrl(this.offer.product.id)
				.setBoards([this.offer.board.id])
				.setOwnTransport(this.offer.transportKind === TransportKind.WITHOUT)
				.setDateRange(this.offer.term.dateRange.lower, this.offer.term.dateRange.upper)
				.setAdults(this.offer.slot.length.toString())
				.setPromotion(this.offer.promotion.map((v) => v.id));

			if (this.offer.roomType) {
				urlBuilder.setRoomType([this.offer.roomType.id]);
			}

			return urlBuilder.build();
		}
	}

	private mapSlot(slot: Slot, i: number): SeatNumberPayload {
		const { doubleDeckDeparture, singleDeckDeparture, doubleDeckReturn, singleDeckReturn } = this.chosenSeats;
		const payload: Partial<SeatNumberPayload> = {};

		this.updateSeatPayload(payload, doubleDeckDeparture, i, 'doubleDeckDeparture');
		this.updateSeatPayload(payload, singleDeckDeparture, i, 'singleDeckDeparture');
		this.updateSeatPayload(payload, doubleDeckReturn, i, 'doubleDeckReturn');
		this.updateSeatPayload(payload, singleDeckReturn, i, 'singleDeckReturn');

		if (deepCompare(payload, {})) {
			return;
		}

		payload.slot = slot.id;

		if (slot.seatnumber) {
			payload.id = slot.seatnumber.id;
		}

		return payload as SeatNumberPayload;
	}

	private updateSeatPayload(
		payload: Partial<SeatNumberPayload>,
		seats: number[],
		index: number,
		name: keyof SeatNumberPayload,
	): void {
		payload[name] = seats[index];
	}

	private getReservationData(): void {
		const { getCancellationReasons } = this.apiService.actions();
		const { getChannels } = this.apiService.actions();
		this.channels = getChannels();

		this.store
			.getters()
			.reservation()
			.pipe(
				filter((value) => !!value),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((result) => {
				this.loaderService.hide();
				this.reservation = result;
				this.voucherCode = (this.reservation?.voucher as Voucher)?.code ?? '';
				this.voucherValue = (this.reservation?.voucher as Voucher)?.methodValue ?? 0;

				const validStatuses = [Status.OPTION, Status.CONFIRMED];

				const matchingInsurer =
					this.reservation.offer.find((selection) => validStatuses.includes(selection.status as Status))
						?.insurer ?? null;

				this.activeReservationInsurer.set(matchingInsurer);

				if (!this.reservation.offer.length)
					this.infobox.error('Wystąpił nieoczekiwany błąd, rezerwacja nie ma podpiętej żadnej oferty');

				this._service.reservation$.next(result);
				this._service.totalPrice = result.totalPrice;
				this._service.currency = result.currency;
				this.comfortPackageDisabled = !this.offer.slot
					.filter((s) => [Status.OPTION, Status.CONFIRMED].includes(s.status as Status))
					.every((s) => s.slotServices.find((s) => s.service.serviceType.id === COMFORT_PACKAGE_SERVICE_ID));
				getCancellationReasons();
			});
	}

	private async refetchReservation(): Promise<void> {
		const { getReservation } = this.apiService.actions();
		await getReservation(this.reservationId);
	}

	private get activeSlotsFromOffers(): Slot[] {
		const offers = this.reservation.offer.filter((v) => !CancellationStatuses.includes(v?.status as Status));
		return offers
			.flatMap((offer) => offer.slot)
			.filter((s) => [Status.OPTION, Status.CONFIRMED].includes(s.status as Status));
	}
}
